import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import theme from '../../styles/theme';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.95);
  z-index: ${theme.zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[6]};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: ${theme.spacing[2]};
  }
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    max-width: 100vw;
    max-height: 100vh;
    width: 100%;
    height: 100%;
    justify-content: center;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  max-width: 100%;
  max-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    max-height: 70vh;
    width: 100%;
  }
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    border-radius: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -${theme.spacing[12]};
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: ${theme.fontSizes.xl};
  cursor: pointer;
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  transition: all 0.2s ease;
  z-index: 3;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    top: ${theme.spacing[2]};
    right: ${theme.spacing[2]};
    background: rgba(0, 0, 0, 0.6);
    border: none;
    min-width: 40px;
    min-height: 40px;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: white;
  font-size: ${theme.fontSizes.xl};
  cursor: pointer;
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  transition: all 0.2s ease;
  z-index: 2;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &.prev {
    left: -${theme.spacing[12]};
  }

  &.next {
    right: -${theme.spacing[12]};
  }

  @media (max-width: 768px) {
    &.prev {
      left: ${theme.spacing[2]};
    }

    &.next {
      right: ${theme.spacing[2]};
    }
    min-width: 40px;
    min-height: 40px;
    padding: ${theme.spacing[2]};
  }
`;

const ImageCounter = styled.div`
  color: white;
  font-size: ${theme.fontSizes.base};
  background: rgba(0, 0, 0, 0.6);
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.full};
  margin-top: ${theme.spacing[4]};

  @media (max-width: 768px) {
    font-size: ${theme.fontSizes.sm};
    padding: ${theme.spacing[1]} ${theme.spacing[3]};
    margin-top: ${theme.spacing[2]};
  }
`;

const Indicators = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[4]};

  @media (max-width: 768px) {
    gap: ${theme.spacing[1]};
    margin-top: ${theme.spacing[2]};
  }
`;

const Indicator = styled.button`
  width: 12px;
  height: 12px;
  border-radius: ${theme.borderRadius.full};
  border: none;
  background: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: white;
    transform: scale(1.2);
  }

  @media (max-width: 768px) {
    width: 10px;
    height: 10px;
  }
`;

const ImageModal = ({ images, isOpen, onClose, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const nextImage = () => {
    if (!images || images.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    if (!images || images.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCurrentIndex(initialIndex);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialIndex]);

  useEffect(() => {
    if (!isOpen || !images || images.length === 0) return;

    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
          break;
        case 'ArrowRight':
          setCurrentIndex((prev) => (prev + 1) % images.length);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, images, onClose]);

  const goToImage = (index) => {
    setCurrentIndex(index);
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!images || images.length === 0 || !isOpen) {
    return null;
  }

  const currentImage = images[currentIndex];

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <FiX />
        </CloseButton>

        <ImageContainer>
          <Image src={currentImage.link} alt={currentImage.alt || `Imagem ${currentIndex + 1}`} />
          
          {images.length > 1 && (
            <>
              <NavigationButton
                className="prev"
                onClick={prevImage}
                disabled={images.length <= 1}
              >
                <FiChevronLeft />
              </NavigationButton>

              <NavigationButton
                className="next"
                onClick={nextImage}
                disabled={images.length <= 1}
              >
                <FiChevronRight />
              </NavigationButton>
            </>
          )}
        </ImageContainer>

        {images.length > 1 && (
          <>
            <ImageCounter>
              {currentIndex + 1} / {images.length}
            </ImageCounter>
            
            <Indicators>
              {images.map((_, index) => (
                <Indicator
                  key={index}
                  $active={index === currentIndex}
                  onClick={() => goToImage(index)}
                />
              ))}
            </Indicators>
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default ImageModal;
